/**
 * Main product section: gallery, accordions, size/quantity pricing and add to cart.
 * Adding to cart is delegated to the theme's <xo-cart-add>, which posts to /cart/add,
 * refreshes the cart sections and opens the cart drawer.
 */
if (!customElements.get('main-product-panel')) {
  class MainProductPanel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const data = JSON.parse(this.querySelector('[data-pdp-json]').textContent);
      this.moneyFormat = data.moneyFormat || '${{amount}}';
      this.productUrl = data.productUrl;
      this.variants = data.variants || [];

      this.priceEl = this.querySelector('[data-pdp-price]');
      this.variantInput = this.querySelector('[data-pdp-variant-input]');
      this.quantityInput = this.querySelector('[data-pdp-quantity-input]');
      this.cartAdd = this.querySelector('[data-pdp-cart-add]');
      this.atcButton = this.querySelector('[data-pdp-atc]');
      this.atcPrice = this.querySelector('[data-pdp-atc-price]');
      this.atcDivider = this.querySelector('.pdp__atc-divider');
      this.atcText = this.querySelector('[data-pdp-atc-text]');
      this.optionFieldsets = Array.from(this.querySelectorAll('[data-pdp-option]'));
      this.quantityRadios = Array.from(this.querySelectorAll('[data-pdp-quantities] input[type="radio"]'));
      this.thumbStrip = this.querySelector('[data-pdp-thumbs]');
      this.sticky = this.querySelector('[data-pdp-sticky]');

      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      if (this.sticky) this.initSticky();

      // Radios can be restored by the browser on back/forward navigation, so sync from the DOM.
      this.currentVariant = this.getSelectedVariant();
      this.syncVariant({ updateUrl: false, updateMedia: false });
    }

    disconnectedCallback() {
      this.stickyObserver?.disconnect();
      this.stickyResizeObserver?.disconnect();
      document.body.classList.remove('pdp-has-sticky');
    }

    /* ---------- Events ---------- */

    onChange(event) {
      const input = event.target;
      if (input.type !== 'radio') return;

      if (input.closest('[data-pdp-option]')) {
        this.currentVariant = this.getSelectedVariant();
        this.syncVariant({ updateUrl: true, updateMedia: true });
      } else if (input.closest('[data-pdp-quantities]')) {
        this.render();
      }
    }

    onClick(event) {
      const thumb = event.target.closest('.pdp-gallery__thumb');
      if (thumb) {
        this.selectMedia(thumb.dataset.mediaId);
        return;
      }

      const trigger = event.target.closest('.pdp-accordion__trigger');
      if (trigger) {
        this.toggleAccordion(trigger.closest('[data-pdp-accordion]'));
        return;
      }

      const guaranteeLink = event.target.closest('[data-pdp-open-accordion]');
      if (guaranteeLink) {
        const accordion = document.getElementById(guaranteeLink.dataset.pdpOpenAccordion);
        if (!accordion) return;
        this.toggleAccordion(accordion, true);
        this.scrollToElement(accordion);
        accordion.querySelector('.pdp-accordion__trigger')?.focus({ preventScroll: true });
        return;
      }

      const reviewsLink = event.target.closest('[data-pdp-reviews-link]');
      if (reviewsLink) {
        const target = this.getReviewsTarget();
        if (!target) return;
        event.preventDefault();
        this.scrollToElement(target);
      }
    }

    /* ---------- Variant + pricing ---------- */

    getSelectedVariant() {
      if (!this.optionFieldsets.length) {
        return this.variants.find((variant) => String(variant.id) === this.variantInput.value) || this.variants[0];
      }
      const selected = this.optionFieldsets.map((fieldset) => fieldset.querySelector('input:checked')?.value);
      return this.variants.find((variant) => selected.every((value, index) => variant.options[index] === value));
    }

    syncVariant({ updateUrl, updateMedia }) {
      const variant = this.currentVariant;

      if (variant) {
        if (this.variantInput.value !== String(variant.id)) {
          this.variantInput.value = variant.id;
          // Lets Shop Pay installments and other form listeners pick up the new variant.
          this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.cartAdd?.setAttribute('xo-variant-id', variant.id);

        if (updateUrl && this.productUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
        if (updateMedia && variant.featured_media) {
          this.selectMedia(variant.featured_media.id);
        }
      }

      this.render();
    }

    getSelectedQuantity() {
      const radio = this.quantityRadios.find((input) => input.checked);
      if (!radio) return { quantity: 1, discount: 0 };
      return {
        quantity: parseInt(radio.dataset.quantity, 10) || 1,
        discount: parseFloat(radio.dataset.discount) || 0,
      };
    }

    getBundlePrice(unitPrice, quantity, discount) {
      const full = unitPrice * quantity;
      const total = Math.round((full * (100 - discount)) / 100);
      return { full, total, perUnit: Math.round(total / quantity), saving: full - total };
    }

    render() {
      const variant = this.currentVariant;
      const { quantity, discount } = this.getSelectedQuantity();

      this.quantityInput.value = quantity;
      this.cartAdd?.setAttribute('xo-product-quantity', quantity);

      if (variant) {
        this.quantityRadios.forEach((radio) => {
          const card = radio.closest('.pdp-qty__card');
          const qty = parseInt(radio.dataset.quantity, 10) || 1;
          const bundle = this.getBundlePrice(variant.price, qty, parseFloat(radio.dataset.discount) || 0);

          this.setText(card, '[data-pdp-total]', this.formatMoney(bundle.total));
          this.setText(card, '[data-pdp-per]', this.formatMoney(bundle.perUnit));
          this.setText(card, '[data-pdp-compare]', this.formatMoney(bundle.full));
          this.setText(card, '[data-pdp-save]', this.formatMoney(bundle.saving, true));
          const savings = card.querySelector('[data-pdp-savings]');
          if (savings) savings.hidden = bundle.saving <= 0;
        });

        const selected = this.getBundlePrice(variant.price, quantity, discount);
        const total = this.formatMoney(selected.total);
        if (this.priceEl) this.priceEl.textContent = total;
        if (this.atcPrice) this.atcPrice.textContent = total;
      }

      const available = Boolean(variant && variant.available);
      if (this.atcButton) this.atcButton.disabled = !available;
      if (this.atcPrice) this.atcPrice.hidden = !available;
      if (this.atcDivider) this.atcDivider.hidden = !available;
      if (this.atcText) this.atcText.textContent = !variant ? 'Unavailable' : available ? 'Add to Cart' : 'Sold out';

      if (this.sticky) this.renderSticky(variant, available);
    }

    setText(root, selector, text) {
      const el = root.querySelector(selector);
      if (el) el.textContent = text;
    }

    formatMoney(cents, trimZeros = false) {
      const placeholder = /\{\{\s*(\w+)\s*\}\}/;
      const key = (this.moneyFormat.match(placeholder) || [])[1] || 'amount';
      const format = (precision, thousands = ',', decimal = '.') => {
        const [integer, fraction] = (cents / 100).toFixed(precision).split('.');
        const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return fraction ? grouped + decimal + fraction : grouped;
      };

      let value;
      switch (key) {
        case 'amount_no_decimals':
          value = format(0);
          break;
        case 'amount_with_comma_separator':
          value = format(2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = format(0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = format(2, "'", '.');
          break;
        case 'amount_with_space_separator':
          value = format(2, ' ', ',');
          break;
        default:
          value = format(2);
      }
      if (trimZeros && cents % 100 === 0) value = value.replace(/[.,]00$/, '');
      return this.moneyFormat.replace(placeholder, value);
    }

    /* ---------- Gallery ---------- */

    selectMedia(mediaId) {
      const id = String(mediaId);
      const slides = this.querySelectorAll('.pdp-gallery__slide');
      if (!Array.from(slides).some((slide) => slide.dataset.mediaId === id)) return;
      slides.forEach((slide) => {
        const active = slide.dataset.mediaId === id;
        if (!active && !slide.hidden) this.pauseMedia(slide);
        if (active && slide.hidden) this.restartMedia(slide);
        slide.hidden = !active;
      });
      this.querySelectorAll('.pdp-gallery__thumb').forEach((thumb) => {
        const active = thumb.dataset.mediaId === id;
        thumb.setAttribute('aria-pressed', active);
        if (active) this.scrollThumbIntoView(thumb);
      });
    }

    pauseMedia(slide) {
      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('iframe').forEach((iframe) => {
        const message = iframe.src.includes('vimeo')
          ? { method: 'pause' }
          : { event: 'command', func: 'pauseVideo', args: '' };
        iframe.contentWindow?.postMessage(JSON.stringify(message), '*');
      });
    }
    restartMedia(slide) {
      slide.querySelectorAll('video').forEach((video) => {
        video.currentTime = 0;
        video.play().catch(() => {});
      });
      slide.querySelectorAll('iframe').forEach((iframe) => {
        const messages = iframe.src.includes('vimeo')
          ? [{ method: 'setCurrentTime', value: 0 }, { method: 'play' }]
          : [
              { event: 'command', func: 'seekTo', args: [0, true] },
              { event: 'command', func: 'playVideo', args: '' },
            ];
        messages.forEach((message) => iframe.contentWindow?.postMessage(JSON.stringify(message), '*'));
      });
    }
    scrollThumbIntoView(thumb) {
      const strip = this.thumbStrip;
      if (!strip || strip.scrollWidth <= strip.clientWidth) return;
      const left = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
      strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }

    /* ---------- Accordions ---------- */

    toggleAccordion(accordion, forceOpen) {
      if (!accordion) return;
      const trigger = accordion.querySelector('.pdp-accordion__trigger');
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      const open = forceOpen ?? trigger.getAttribute('aria-expanded') !== 'true';
      trigger.setAttribute('aria-expanded', open);
      panel.hidden = !open;
    }

    /* ---------- Sticky add to cart (mobile) ---------- */

    initSticky() {
      this.stickyButton = this.sticky.querySelector('[data-pdp-sticky-atc]');
      // Reuse the main button so the theme's <xo-cart-add> handles the add and opens the cart drawer.
      this.stickyButton.addEventListener('click', () => this.atcButton?.click());

      document.body.classList.add('pdp-has-sticky');
      this.stickyResizeObserver = new ResizeObserver(() => {
        const height = this.sticky.offsetHeight;
        if (height) document.body.style.setProperty('--pdp-sticky-height', `${height}px`);
      });
      this.stickyResizeObserver.observe(this.sticky);

      const form = this.querySelector('.pdp__form');
      if (!form) return;
      this.stickyObserver = new IntersectionObserver(([entry]) => {
        const visible = !entry.isIntersecting;
        this.sticky.classList.toggle('is-visible', visible);
        this.sticky.inert = !visible;
      });
      this.stickyObserver.observe(form);
    }

    renderSticky(variant, available) {
      const price = this.sticky.querySelector('[data-pdp-sticky-price]');
      const divider = this.sticky.querySelector('.pdp__atc-divider');
      const text = this.sticky.querySelector('[data-pdp-sticky-text]');
      const summary = this.sticky.querySelector('[data-pdp-sticky-summary]');

      if (price && this.atcPrice) price.textContent = this.atcPrice.textContent;
      if (price) price.hidden = !available;
      if (divider) divider.hidden = !available;
      if (text && this.atcText) text.textContent = this.atcText.textContent;
      this.stickyButton.disabled = !available;

      if (summary) {
        const options = variant ? variant.options.filter((value) => value !== 'Default Title') : [];
        const quantityLabel = this.quantityRadios
          .find((input) => input.checked)
          ?.closest('.pdp-qty__card')
          ?.querySelector('.pdp-qty__label')
          ?.textContent.trim()
          .toLowerCase();
        summary.textContent = [...options, quantityLabel].filter(Boolean).join(', ');
      }
    }

    /* ---------- Scrolling ---------- */

    getReviewsTarget() {
      const selector = this.dataset.reviewsSelector;
      if (selector) {
        try {
          const custom = document.querySelector(selector);
          if (custom) return custom;
        } catch (error) {
          // Invalid selector from settings; fall back to the defaults below.
        }
      }
      return document.querySelector(
        '[id^="shopify-section-"][id*="__custom_product_reviews"], .custom-product-reviews-list-section, #reviews'
      );
    }

    scrollToElement(element) {
      const header = document.querySelector('.shopify-section-group-header-group xo-sticky, xo-sticky');
      const offset = (header ? header.getBoundingClientRect().height : 0) + 16;
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }

  customElements.define('main-product-panel', MainProductPanel);
}
